import { Component, HostBinding, ViewEncapsulation, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, fromEvent } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { GridApi, GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { OrganizationService } from '../../../services/organization.service';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { CommonColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { TranslationService } from '../../../../shared/services/translation.service';
import { OrganizationServiceResponse } from '../../../models/organization-service-info';
import { UserService } from '../../../../shared/services/user.service';
import { LoadingSpinnerCellComponent }
	from '../../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { DialogDeleteServiceComponent }
	from '../../user-card/self/dialog-delete-service/dialog-delete-service.component';
import { DialogShowParametersComponent }
	from '../../user-card/self/dialog-show-user-parameters/dialog-show-parameters.component';
import { ParameterIdResponse } from '../../../models/v2/parameter-id-response';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';

const resizeObservable = fromEvent(globalThis, 'resize');
const orgServiceFields = [
	'id',
	'service.id',
	'service.labelTranslationId',
	'service.descriptionTransId',
	'service.manageParameter',
	'supplier.id',
	'supplier.name',
	'serviceParameters.id',
	'serviceParameters.value',
	'serviceParameters.parameter.id',
	'serviceParameters.parameter.paramName',
	'serviceParameters.parameter.defaultValue',
	'serviceParameters.parameter.description'
];

@Component({
	selector: 'copo-organization-card-services',
	templateUrl: './organization-card-services.component.html',
	styleUrl: './organization-card-services.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, MatButtonModule, TranslateModule, MatIconModule, AgGridModule]
})
export class OrganizationCardServicesComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@Input() orgaParentId?: number;
	services: OrganizationServiceResponse[] = [];
	parametersRequest?: ParameterIdResponse;
	updateParameterRequestList?: ParameterIdResponse[];
	addParameterRequestList?: ParameterIdResponse[];
	id?: string;
	error = false;
	memberId = 0;
	parameter = false;
	readonly modules = clientSideAgGridModules;
	gridApi?: GridApi<OrganizationServiceResponse>;
	gridOptions: CommonGridOptions<OrganizationServiceResponse>;

	enableManageServices? = false;

	private routeSub: Subscription = Subscription.EMPTY;
	private organizationSub: Subscription = Subscription.EMPTY;
	private deleteServiceSub: Subscription = Subscription.EMPTY;
	private dialogSubscription: Subscription = new Subscription();
	private resizeSub?: Subscription;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly route: ActivatedRoute,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly router: Router,

		private readonly dialog: MatDialog,
	) {
		this.gridOptions = new CommonGridOptions<OrganizationServiceResponse>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			domLayout: 'autoHeight',
			headerHeight: 50,
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			columnDefs: [
				new CommonColDef<OrganizationServiceResponse, string>({
					headerName: this.translationService.getTR('user.card.services.tab.name'),
					headerComponent: CheckboxHeaderComponent,
					field: 'service.labelTranslationId',
					sort: 'asc',
					sortable: true,
					comparator: (valueA, valueB) =>
						!valueA ? -1 : !valueB ? 1 : valueA.toString().localeCompare(valueB.toString()),
				}),
				new CommonColDef<OrganizationServiceResponse>({
					headerName: this.translationService.getTR('user.card.services.tab.desc'),
					field: 'service.descriptionTransId',
				}),
				new IconColDef<OrganizationServiceResponse>({
					minWidth: 80,
					maxWidth: 100,
					width: 100,
					cellStyle: { textAlign: 'center' },
					cellRendererParams: {
						icon: (params: ICellRendererParams<OrganizationServiceResponse>) => {
							const serviceResponse = params.data;
							if (!serviceResponse) {
								return '';
							}
							if (serviceResponse.diffusionServiceParameters && this.enableManageServices
								&& serviceResponse.service.manageParameter === 1) {
								return {
									iconName: 'settings',
									className: 'mat-accent',
								};
							}
							return '';
						},
						onClick: (params: ICellRendererParams<OrganizationServiceResponse>) => () => {
							this.showUserParameterDialog(params.api, params.node.id);
						}
					},
				}),
				new IconColDef<OrganizationServiceResponse>({
					headerName: this.translationService.getTR('organization.user-manage'),
					minWidth: 64,
					maxWidth: 64,
					width: 64,
					cellStyle: { textAlign: 'center' },
					cellRendererParams: {
						icon: () => {
							if (this.enableManageServices) {
								return {
									iconName: 'delete_bin',
									className: 'mat-accent',
								};
							}
							return '';
						},
						onClick: (params: ICellRendererParams<OrganizationServiceResponse>) => () => {
							this.showDeletionDialog(params);
						}
					},
				})
			]
		});
	}

	ngOnInit() {
		this.error = false;
		if (this.route.parent === null) {
			return;
		}

		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const memberId = params.get('id');
			if (memberId === null) {
				return;
			}
			this.setServices(memberId);
			this.memberId = Number(memberId);
		});

		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}
		this.enableManageServices = loginUser.canUpdateOrganizationServices(this.memberId);
	}

	ngOnDestroy() {
		this.routeSub.unsubscribe();
		this.organizationSub.unsubscribe();
		this.dialogSubscription.unsubscribe();
		this.deleteServiceSub.unsubscribe();
		if (this.resizeSub) {
			this.resizeSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	showDeletionDialog(event: ICellRendererParams<OrganizationServiceResponse>) {
		if (event.node.id) {
			const service = event.api.getRowNode(event.node.id)?.data;
			if (!service) {
				return;
			}
			const dialogRef = this.dialog.open(DialogDeleteServiceComponent, {
				width: '60%',
				data: service.service.labelTranslationId
			});

			this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
				if (result && service.service.id) {
					this.deleteService(service);
				}
			});
		}
	}

	showUserParameterDialog(api: GridApi, nodeId?: string) {
		if (nodeId) {
			const orgaServicesResponse = <OrganizationServiceResponse>api.getRowNode(nodeId)?.data;
			const dialogRef = this.dialog.open(DialogShowParametersComponent, {
				width: '60%',
				data: orgaServicesResponse.diffusionServiceParameters,
			});

			this.dialogSubscription = dialogRef.afterClosed().subscribe(async result => {
				if (!result) {
					return;
				}
				const showParameterResult = <Record<string, string> | boolean> result;
				this.addParameterRequestList = [];
				this.updateParameterRequestList = [];
				if (!orgaServicesResponse.diffusionServiceParameters) {
					return;
				}
				for (const diffusionServiceParameter of orgaServicesResponse.diffusionServiceParameters) {
					for (const parameterResult of Object.entries(showParameterResult)) {
						if (String(diffusionServiceParameter.serviceParameter.id) === parameterResult[0]) {
							if (!diffusionServiceParameter.value && diffusionServiceParameter.value !== '') {
								diffusionServiceParameter.value = String(parameterResult[1]);
								this.parametersRequest = {
									id: diffusionServiceParameter.serviceParameter.id,
									value: String(parameterResult[1]),
								};
								this.addParameterRequestList.push(this.parametersRequest);
							} else if (diffusionServiceParameter.value !== parameterResult[1]) {
								this.parametersRequest = {
									id: diffusionServiceParameter.serviceParameter.id,
									value: String(parameterResult[1]),
								};
								this.updateParameterRequestList.push(this.parametersRequest);
							}
						}
					}
				}
				await this.addServiceParameters(
					this.memberId,
					orgaServicesResponse.service.id,
					this.addParameterRequestList
				);
				await this.updateServiceParameters(
					this.memberId,
					orgaServicesResponse.service.id,
					this.updateParameterRequestList
				);
			});
		}
	}

	async updateServiceParameters(orgId: number, serviceId: string, parametersRequest: ParameterIdResponse[]) {
		try {
			await this.organizationService.updateServiceParametersOfOrganization(orgId, serviceId, parametersRequest)
				.then(() => {
					this.setServices(String(orgId));
				});
		} catch (err) {
			console.error('Error while updating the parameter value', err);
		}
	}

	async addServiceParameters(orgId: number, serviceId: string, parametersRequest: ParameterIdResponse[]) {
		try {
			await this.organizationService.addServiceParametersToOrganization(orgId, serviceId, parametersRequest)
				.then(() => {
					this.setServices(String(orgId));
				});
		} catch (err) {
			console.error('Error while adding the parameter value', err);
		}
	}

	async goToManageService() {
		await this.router.navigate(['user/organization', this.memberId, 'manage-service']);
	}

	private setServices(organizationId: string) {
		this.organizationSub = this.organizationService.getOrganizationServices(organizationId, orgServiceFields)
			.subscribe({
				next: services => {
					this.services = services.results;
					if (this.services.length > 0) {
						this.services.sort((a, b) => a.service.labelTranslationId.localeCompare(
							b.service.labelTranslationId
						));
					}
					this.gridApi?.setGridOption('rowData', this.services);
				},
				error: (err: unknown) => {
					this.error = true;
					console.error(err);
				}
			});
	}

	private deleteService(serviceToDelete: OrganizationServiceResponse) {
		this.deleteServiceSub = this.organizationService
			.deleteServicesFromOrganization(
				String(this.memberId),
				serviceToDelete.supplier.id,
				[serviceToDelete.service.id]
			)
			.subscribe({
				next: () => {
					const index = this.services.findIndex(service => service.service.id === serviceToDelete.service.id);
					this.services.splice(index, 1);
					if (this.gridApi) {
						this.gridApi.setGridOption('rowData', this.services);
					}
				},
				error: (err: unknown) => {
					console.error(err);
				}
			});
	}
}
